import { apiGetLibrary, apiPutLibrary, isLoggedIn } from "./api";

import type {
  LibraryPersistedData,
  LibraryPersistenceAdapter,
} from "@excalidraw/excalidraw/data/library";
import type { LibraryItems } from "@excalidraw/excalidraw/types";

/**
 * Persists the Excalidraw library into the Techify backend (PostgreSQL).
 *
 * The library is global / shared across all authenticated users — there is no
 * per-user filter, so every load/save reflects the same canonical state.
 *
 * Implements LibraryPersistenceAdapter — sits behind useHandleLibrary.
 */
export const LibraryServerAdapter = {
  async load(_metadata?: {
    source?: "load" | "save";
  }): Promise<LibraryPersistedData | null> {
    if (!isLoggedIn()) {
      return null;
    }
    try {
      const libraryItems = await apiGetLibrary();
      // ApiLibraryItem is structurally compatible with LibraryItem; the only
      // difference is that the server returns elements as `any[]`. The native
      // library code restores / validates element shape on its own, so a cast
      // is safe here.
      return { libraryItems: libraryItems as unknown as LibraryItems };
    } catch (err) {
      console.error("LibraryServerAdapter.load failed", err);
      return null;
    }
  },

  async save(data: LibraryPersistedData): Promise<void> {
    if (!isLoggedIn()) {
      return;
    }
    await apiPutLibrary(data.libraryItems as any);
  },
} satisfies LibraryPersistenceAdapter;
